import _ from 'lodash'
import { ItListItem } from 'types/graphql'

import { DefaultValues } from '@redwoodjs/forms'

import { platformURLs } from 'src/lib/platforms'
import { matchUrl } from 'src/lib/utils'

type TestItem = DefaultValues<ItListItem>

/**
 * @manual Inside section of a Locked ItList.
 *
 * To add a new operator, add a new key to the type Statistic and a new entry in the const `operators`.
 * Each operator contains a test function and a count.
 * If the test function returns true the count will increment.
 * ItListItem which have failed to trigger any operator will be added to the 'other' statistic.
 */

type TestFn = (item: TestItem) => boolean

/** @manual Add here a key for each stats we want to compute. */
export type Statistic =
  | 'exclusiveContent'
  | 'voiceTip'
  | 'audio'
  | 'pdf'
  | 'product'
  | 'image'
  | keyof typeof platformURLs

type Operator = {
  count: number
  label: Statistic
  test: TestFn
}

export type Statistics = { label: string; count: number }[]

export function getItListItemsCountByType(items: ItListItem[]): Statistics {
  let other = 0
  const computeStatistics = items.reduce(
    (stats: Operator[], item: TestItem) => {
      let isAccountedFor = false
      const parsedStats = stats.map((operator) => {
        const result = operator.test(item)
        if (result && operator.label !== 'voiceTip') {
          isAccountedFor = true
        }
        return {
          ...operator,
          count: result ? operator.count + 1 : operator.count,
        }
      })
      if (!isAccountedFor && item.type !== 'text') {
        other += 1
      }
      return parsedStats
    },
    operators
  )
  return [
    ...computeStatistics.map((st) => _.omit(st, 'testFn')),
    { label: 'other', count: other },
  ]
}

const contentTest =
  (category: keyof typeof platformURLs): TestFn =>
  (item) =>
    item.type === 'content' &&
    platformURLs[category].some((p) => matchUrl(p, item.url))

/**
 * This object contains all operators to compute the statistics for the Inside section.
 * @manual A new operator only needs to be added here and in types to work properly.
 * @warning Order matters.
 */
const customOperators: Operator[] = [
  {
    label: 'exclusiveContent',
    test: (item) =>
      (item[item.type] as { onlyAvailableOnItList: boolean })
        ?.onlyAvailableOnItList,
    count: 0,
  },
  {
    label: 'voiceTip',
    test: (item) => item.hasVoiceTip,
    count: 0,
  },
  {
    label: 'audio',
    test: (item) => item.type === 'voiceTip',
    count: 0,
  },
  {
    label: 'pdf',
    test: (item) =>
      item.type === 'document' && item.document?.fileName?.endsWith('.pdf'),
    count: 0,
  },
  {
    label: 'product',
    test: (item) => item.type === 'product',
    count: 0,
  },
  {
    label: 'image',
    test: (item) => item.type === 'image',
    count: 0,
  },
]
const standardOperators: Operator[] = (
  Object.keys(platformURLs) as (keyof typeof platformURLs)[]
).map((category) => ({
  label: category,
  test: contentTest(category),
  count: 0,
}))

const operators: Operator[] = [...customOperators, ...standardOperators].reduce(
  (acc, operator) => {
    const existingOperator = acc.find((op) => op.label === operator.label)
    if (existingOperator) {
      const copyOfExistingOperator = { ...existingOperator }
      existingOperator.test = (item) =>
        copyOfExistingOperator.test(item) || operator.test(item)
      return acc
    }
    return [...acc, operator]
  },
  [] as Operator[]
)
